import { useEffect, useState } from "react";
import { useCustom } from "@refinedev/core";

import { fetchKennelAvatarObjectUrl } from "../../api/kennelAvatar";

// kp-Epic 7 (kennelplanning-migration) — frontend part 1: the StayHistory drawer body. Rendered inside
// the page-local <Drawer>. Given a dierId it fetches the stay-history feed (and, on demand, the
// contact-trace feed) and renders at parity with the Jmix `StayHistoryDrawer` LAYOUT:
//   LEFT  — avatar (reuses the screen's bearer-aware avatar-by-dierId) + dierNaam + an "Aandachtspunten"
//           block (medischAlgemeen / bijzonderheden) + an action row (Besmettingsonderzoek, optional
//           Open dierdossier).
//   RIGHT — a "Verblijfshistorie" timeline of `periodes` (newest-first), and — when contacts are shown —
//           a "Contactonderzoek" list.
// D1: REAL data only. The Jmix drawer hardcoded vaccine/weight/note demo values; we deliberately do NOT
// fabricate those — only fields the contract actually returns are rendered.
//
//   GET kennelplanning/animals/{dierId}/stay-history  → StayHistoryResponse
//   GET kennelplanning/contact-trace?dierId=          → ContactTraceResponse
// Both read via Refine `useCustom` against RELATIVE urls (the dataProvider prepends `/api`), defensive
// against partial/empty payloads.

/** The translate signature (positional %s/%d via `{ args: [...] }`). */
type Translate = (key: string, options?: { args?: unknown[] }, defaultMessage?: string) => string;

// ── Wire contract types ─────────────────────────────────────────────────────────────────────────────

interface Aandachtspunten {
  medischAlgemeen?: string | null;
  bijzonderheden?: string | null;
}

interface StayPeriode {
  kennelAsielId?: number | null;
  kennelNummer?: string | null;
  roomNaam?: string | null;
  vanDatum?: string | null;
  totDatum?: string | null;
  createdByUsername?: string | null;
  updatedByUsername?: string | null;
}

/** Latest weight measurement (kp-Epic 7 final). */
interface StayGewicht {
  waarde?: number | null;
  datum?: string | null;
}

/** Latest vaccination (kp-Epic 7 final). */
interface StayEnting {
  naam?: string | null;
  datum?: string | null;
}

/** A recent note (kp-Epic 7 final), newest-first per the contract. */
interface StayNotitie {
  tekst?: string | null;
  auteur?: string | null;
  datum?: string | null;
}

interface StayHistoryResponse {
  dierId?: number | null;
  dierNaam?: string | null;
  aandachtspunten?: Aandachtspunten | null;
  periodes?: StayPeriode[] | null;
  // kp-Epic 7 (final): extra detail blocks — all optional / null when no data exists.
  gewicht?: StayGewicht | null;
  enting?: StayEnting | null;
  voedingCategorie?: string | null;
  recenteNotities?: StayNotitie[] | null;
}

interface TraceContact {
  dierId?: number | null;
  dierNaam?: string | null;
  contactType?: "SAME_KENNEL" | "NEIGHBOUR" | null;
  daysOverlap?: number | null;
  kennelAsielId?: number | null;
  kennelGroepAsielId?: number | null;
}

interface ContactTraceResponse {
  sourceDierId?: number | null;
  sourceKennelIds?: number[] | null;
  neighbourKennelIds?: number[] | null;
  sourceGroepIds?: number[] | null;
  contacten?: TraceContact[] | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────────────────────────

function initialsOf(naam: string | null | undefined): string {
  if (!naam || !naam.trim()) return "?";
  const trimmed = naam.trim();
  return trimmed.length === 1 ? trimmed.toUpperCase() : trimmed.slice(0, 2).toUpperCase();
}

/** Format an ISO date (YYYY-MM-DD or full ISO) as DD-MM-YYYY; passthrough when unparseable. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

// ── Drawer avatar (bearer-aware blob → object URL; falls back to initials) ──────────────────────────
// Reuses the screen's shared per-dier avatar fetch (kennelAvatar.ts) so the drawer shows the same image
// as the floorplan tile. The object URL is owned/cached by kennelAvatar.ts (shared per dier), so we do
// NOT revoke it on unmount.

function DrawerAvatar({ dierId, naam }: { dierId: number | null; naam: string | null | undefined }) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    setFailed(false);
    setObjectUrl(null);
    if (dierId != null) {
      void fetchKennelAvatarObjectUrl(dierId).then((url) => {
        if (live) setObjectUrl(url);
      });
    }
    return () => {
      live = false;
    };
  }, [dierId]);

  const showImg = !!objectUrl && !failed;
  return (
    <div className="kp-drawer__avatar">
      {showImg ? (
        <img
          className="kp-drawer__avatar-img"
          src={objectUrl ?? undefined}
          alt={naam ?? ""}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="kp-drawer__avatar-initials">{initialsOf(naam)}</span>
      )}
    </div>
  );
}

// ── Component ───────────────────────────────────────────────────────────────────────────────────────

export interface StayHistoryDrawerProps {
  /** The dier whose stay history to show. */
  dierId: number;
  /** Whether the module/fetch is enabled. */
  enabled?: boolean;
  /** Start a contact trace for this dier (parent sets traceDierId + switches to TIMELINE + closes). */
  onStartTrace: (dierId: number, dierNaam: string | null) => void;
  /** Optional: open the full animal dossier. When omitted, the "Open dierdossier" button is hidden. */
  onOpenAnimal?: (dierId: number) => void;
  t: Translate;
}

export function StayHistoryDrawer({
  dierId,
  enabled = true,
  onStartTrace,
  onOpenAnimal,
  t,
}: StayHistoryDrawerProps) {
  // Whether the on-demand "Contactonderzoek" list is shown (fetches contact-trace for this dier).
  const [showContacts, setShowContacts] = useState(false);

  // Reset the contacts toggle whenever the drawer switches to a different dier.
  useEffect(() => {
    setShowContacts(false);
  }, [dierId]);

  const { data: historyData, isLoading, isError } = useCustom<StayHistoryResponse>({
    url: `kennelplanning/animals/${dierId}/stay-history`,
    method: "get",
    queryOptions: { enabled, retry: false },
  });

  const { data: traceData } = useCustom<ContactTraceResponse>({
    url: `kennelplanning/contact-trace?dierId=${dierId}`,
    method: "get",
    queryOptions: { enabled: enabled && showContacts, retry: false },
  });

  const history = historyData?.data ?? null;
  const dierNaam = history?.dierNaam ?? null;
  const aandachtspunten = history?.aandachtspunten ?? null;
  const periodes = history?.periodes ?? [];
  const medisch = aandachtspunten?.medischAlgemeen?.trim() || null;
  const bijzonderheden = aandachtspunten?.bijzonderheden?.trim() || null;
  const hasAandachtspunten = medisch != null || bijzonderheden != null;

  // kp-Epic 7 (final): the extra detail blocks. REAL data only — render nothing we don't have.
  const gewicht = history?.gewicht?.waarde != null ? history.gewicht : null;
  const enting = history?.enting?.naam?.trim() ? history.enting : null;
  const voeding = history?.voedingCategorie?.trim() || null;
  const recenteNotities = history?.recenteNotities ?? [];

  const contacten = showContacts ? traceData?.data?.contacten ?? [] : [];

  return (
    <div className="kp-drawer__body">
      {/* ── LEFT: identity + aandachtspunten + actions ── */}
      <div className="kp-drawer__left">
        <div className="kp-drawer__avatar-block">
          <DrawerAvatar dierId={dierId} naam={dierNaam} />
        </div>

        <div className="kp-drawer__identity">
          <h3 className="kp-drawer__name">{dierNaam ?? ""}</h3>
        </div>

        <div className="kp-drawer__card">
          <div className="kp-drawer__card-head">
            <span className="kp-drawer__card-title">
              {t("kennelplanning.historyDrawer.aandachtspunten")}
            </span>
          </div>
          {hasAandachtspunten ? (
            <div className="kp-drawer__aandachtspunten">
              {medisch != null && (
                <div className="kp-drawer__aandachtspunt">
                  <span className="kp-drawer__tag kp-drawer__tag--peach">
                    {t("kennelplanning.historyDrawer.medisch")}
                  </span>
                  <span className="kp-drawer__aandachtspunt-text">{medisch}</span>
                </div>
              )}
              {bijzonderheden != null && (
                <div className="kp-drawer__aandachtspunt">
                  <span className="kp-drawer__tag kp-drawer__tag--blue">
                    {t("kennelplanning.historyDrawer.bijzonderheden")}
                  </span>
                  <span className="kp-drawer__aandachtspunt-text">{bijzonderheden}</span>
                </div>
              )}
            </div>
          ) : (
            <span className="kp-drawer__muted">
              {t("kennelplanning.historyDrawer.geenAandachtspunten")}
            </span>
          )}
        </div>

        <div className="kp-drawer__spacer" />

        <div className="kp-drawer__actions">
          {onOpenAnimal && (
            <button
              type="button"
              className="kp-drawer__open-dossier"
              onClick={() => onOpenAnimal(dierId)}
            >
              {t("kennelplanning.historyDrawer.openDossier")}
            </button>
          )}
          {/* Besmettingsonderzoek banner — hands off to the kp-6 trace + closes the drawer (parent). */}
          <button
            type="button"
            className="kp-drawer__besmetting-banner"
            onClick={() => onStartTrace(dierId, dierNaam)}
          >
            <span className="kp-drawer__besmetting-text">
              <span className="kp-drawer__besmetting-title">
                {t("kennelplanning.historyDrawer.besmettingsonderzoek")}
              </span>
              <span className="kp-drawer__besmetting-sub">
                {t("kennelplanning.historyDrawer.besmettingsSub", { args: [dierNaam ?? ""] })}
              </span>
            </span>
          </button>
        </div>
      </div>

      {/* ── RIGHT: verblijfshistorie (+ optional contactonderzoek) ── */}
      <div className="kp-drawer__right">
        <div className="kp-drawer__card">
          <div className="kp-drawer__card-head">
            <span className="kp-drawer__card-title">
              {t("kennelplanning.historyDrawer.verblijfshistorie")}
            </span>
          </div>

          {isLoading ? (
            <span className="kp-drawer__muted">{t("common.loading")}</span>
          ) : isError ? (
            <span className="kp-drawer__muted">{t("kennelplanning.loadFailed")}</span>
          ) : periodes.length === 0 ? (
            <span className="kp-drawer__muted">
              {t("kennelplanning.historyDrawer.geenHistorie")}
            </span>
          ) : (
            <ol className="kp-drawer__timeline">
              {periodes.map((p, i) => {
                const van = formatDate(p.vanDatum);
                const tot = p.totDatum
                  ? formatDate(p.totDatum)
                  : t("kennelplanning.historyDrawer.heden");
                const door = p.createdByUsername?.trim() || null;
                return (
                  <li
                    key={`${p.kennelAsielId ?? "k"}-${p.vanDatum ?? ""}-${i}`}
                    className="kp-drawer__timeline-item"
                  >
                    <span className="kp-drawer__timeline-dot" />
                    <div className="kp-drawer__timeline-content">
                      <span className="kp-drawer__timeline-room">
                        {[p.roomNaam, p.kennelNummer].filter(Boolean).join(" · ")}
                      </span>
                      <span className="kp-drawer__timeline-dates">
                        {van} – {tot}
                      </span>
                      {door && (
                        <span className="kp-drawer__timeline-by">
                          {t("kennelplanning.historyDrawer.door", { args: [door] })}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        {/* kp-Epic 7 (final): Gewicht / Enting / Voeding — only when the contract supplies them. */}
        {(gewicht || enting || voeding) && (
          <div className="kp-drawer__card">
            <dl className="kp-drawer__detail-list">
              {gewicht && (
                <div className="kp-drawer__detail-row">
                  <dt className="kp-drawer__detail-label">
                    {t("kennelplanning.historyDrawer.gewicht")}
                  </dt>
                  <dd className="kp-drawer__detail-value">
                    {gewicht.waarde} {t("kennelplanning.fieldSettings.unit.kg")}
                    {gewicht.datum ? (
                      <span className="kp-drawer__detail-date"> · {formatDate(gewicht.datum)}</span>
                    ) : null}
                  </dd>
                </div>
              )}
              {enting && (
                <div className="kp-drawer__detail-row">
                  <dt className="kp-drawer__detail-label">
                    {t("kennelplanning.historyDrawer.enting")}
                  </dt>
                  <dd className="kp-drawer__detail-value">
                    {enting.naam}
                    {enting.datum ? (
                      <span className="kp-drawer__detail-date"> · {formatDate(enting.datum)}</span>
                    ) : null}
                  </dd>
                </div>
              )}
              {voeding && (
                <div className="kp-drawer__detail-row">
                  <dt className="kp-drawer__detail-label">
                    {t("kennelplanning.historyDrawer.voeding")}
                  </dt>
                  <dd className="kp-drawer__detail-value">{voeding}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {/* kp-Epic 7 (final): Recente notities — newest-first; muted empty state when none. */}
        <div className="kp-drawer__card">
          <div className="kp-drawer__card-head">
            <span className="kp-drawer__card-title">
              {t("kennelplanning.historyDrawer.recenteNotities")}
            </span>
          </div>
          {recenteNotities.length === 0 ? (
            <span className="kp-drawer__muted">
              {t("kennelplanning.historyDrawer.geenNotities")}
            </span>
          ) : (
            <ul className="kp-drawer__notes">
              {recenteNotities.map((n, i) => {
                const tekst = n.tekst?.trim() || null;
                if (!tekst) return null;
                const auteur = n.auteur?.trim() || null;
                const datum = n.datum ? formatDate(n.datum) : null;
                const meta = [auteur, datum].filter(Boolean).join(" · ");
                return (
                  <li key={`${n.datum ?? ""}-${i}`} className="kp-drawer__note">
                    <span className="kp-drawer__note-text">{tekst}</span>
                    {meta && <span className="kp-drawer__note-meta">— {meta}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Contactonderzoek — fetched on demand for THIS dier. */}
        <div className="kp-drawer__card">
          <div className="kp-drawer__card-head kp-drawer__card-head--with-action">
            <span className="kp-drawer__card-title">
              {t("kennelplanning.historyDrawer.contactonderzoek")}
            </span>
            <button
              type="button"
              className="kp-drawer__card-action"
              aria-pressed={showContacts}
              onClick={() => setShowContacts((v) => !v)}
            >
              {showContacts
                ? t("kennelplanning.historyDrawer.contactenVerbergen")
                : t("kennelplanning.historyDrawer.contactenTonen")}
            </button>
          </div>

          {showContacts &&
            (contacten.length === 0 ? (
              <span className="kp-drawer__muted">
                {t("kennelplanning.historyDrawer.geenContacten")}
              </span>
            ) : (
              <ul className="kp-drawer__contacts">
                {contacten.map((c, i) => {
                  const same = c.contactType === "SAME_KENNEL";
                  return (
                    <li
                      key={`${c.dierId ?? "d"}-${i}`}
                      className="kp-drawer__contact"
                    >
                      <span
                        className={
                          "kp-drawer__contact-badge" +
                          (same
                            ? " kp-drawer__contact-badge--same"
                            : " kp-drawer__contact-badge--neighbour")
                        }
                      >
                        {same
                          ? t("kennelplanning.historyDrawer.zelfdeKennel")
                          : t("kennelplanning.historyDrawer.buur")}
                      </span>
                      <span className="kp-drawer__contact-name">{c.dierNaam ?? ""}</span>
                      <span className="kp-drawer__contact-overlap">
                        {t("kennelplanning.historyDrawer.overlap", { args: [c.daysOverlap ?? 0] })}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ))}
        </div>
      </div>
    </div>
  );
}
